import { ReactNode, useEffect } from "react";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
} from "react-router-dom";
import AuthShell from "./AuthShell.tsx";
import { AUTH_ROUTES } from "./authHelpers.ts";
import useAnalytics from "@/services/analytics/useAnalytics.ts";
import GoogleTagManagerScript from "@/common/GoogleTagManagerScript";
import SignInScreen from "./screens/SignInScreen.tsx";
import SignUpScreen from "./screens/SignUpScreen.tsx";
import EmailCodeScreen from "./screens/EmailCodeScreen.tsx";
import { useAuthSurface } from "./authSurface.ts";
import { useAuth } from "./AuthContext.tsx";
import { rememberReturnPath } from "./returnPath.ts";
import { AUDIT_PATH, auditTokenOf } from "../audit/auditProspect.ts";
import {
    AuditProspectProvider,
    useAuditProspect,
} from "../audit/AuditProspectContext.tsx";
import AuditEditPage from "../audit/AuditEditPage.tsx";
import AuditLandingScreen from "../audit/AuditLandingScreen.tsx";
import AuditProfilePanel from "../audit/AuditProfilePanel.tsx";

function AnalyticsPageTracker() {
    const { pathname } = useLocation();
    const analytics = useAnalytics();
    useEffect(
        () => {
            analytics?.page();
        },
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [pathname],
    );
    return null;
}

/** Any other path: sign in first, then come back to it (returnPath.ts). */
function ToSignIn() {
    const location = useLocation();
    useEffect(() => rememberReturnPath(location), [location]);
    return <Navigate to={AUTH_ROUTES.signIn} replace />;
}

/**
 * The shell around a signed-out screen. An audit link's landing screen has
 * the drafted profile where the brand panel is, and keeps it through the code
 * step that follows. Its editor is wider than the auth card, so that one has
 * a page of its own.
 */
function Shell({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    const { openedToken } = useAuditProspect();
    const onAuditLink = auditTokenOf(pathname) !== null;
    if (onAuditLink && pathname.replace(/\/$/, "").endsWith("/edit")) {
        return <>{children}</>;
    }
    const onCodeStepOfAudit =
        openedToken !== null &&
        (pathname === AUTH_ROUTES.confirmSignUp ||
            pathname === AUTH_ROUTES.emailCode);

    return (
        <AuthShell
            rightPanel={
                onAuditLink || onCodeStepOfAudit ? (
                    <AuditProfilePanel />
                ) : undefined
            }
        >
            {children}
        </AuthShell>
    );
}

/**
 * The unauthenticated auth screens, routed with react-router. Branches on the
 * `nextStep` discriminator returned by Amplify's headless `signIn`/`signUp` by
 * navigating between real URLs. Replaces the `@aws-amplify/ui-react`
 * `<Authenticator>`.
 */
export default function AuthFlow() {
    const { signUp, marketing } = useAuthSurface();
    const { oauthError, clearOauthError } = useAuth();

    return (
        <BrowserRouter>
            {marketing && <GoogleTagManagerScript />}
            <AnalyticsPageTracker />
            <AuditProspectProvider>
                <Shell>
                    <Routes>
                        <Route
                            path={AUTH_ROUTES.signIn}
                            element={
                                <SignInScreen
                                    redirectError={oauthError}
                                    onDismissRedirectError={clearOauthError}
                                />
                            }
                        />
                        {signUp && (
                            <Route
                                path={AUTH_ROUTES.signUp}
                                element={<SignUpScreen />}
                            />
                        )}
                        {signUp && (
                            <Route
                                path={AUTH_ROUTES.signUpUtmScout}
                                element={<SignUpScreen />}
                            />
                        )}
                        {signUp && (
                            <Route
                                path={AUTH_ROUTES.confirmSignUp}
                                element={<EmailCodeScreen purpose="sign-up" />}
                            />
                        )}
                        <Route
                            path={AUTH_ROUTES.emailCode}
                            element={<EmailCodeScreen purpose="sign-in" />}
                        />
                        {signUp && (
                            <Route
                                path={`${AUDIT_PATH}/:token`}
                                element={<AuditLandingScreen />}
                            />
                        )}
                        {signUp && (
                            <Route
                                path={`${AUDIT_PATH}/:token/edit`}
                                element={<AuditEditPage />}
                            />
                        )}
                        <Route path="*" element={<ToSignIn />} />
                    </Routes>
                </Shell>
            </AuditProspectProvider>
        </BrowserRouter>
    );
}
